import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import LoggedInRoute from '@/guards/LoggedInRoute';
import HeaderLayout from '@/layout/HeaderLayout';
import BackgroundImageLayout from '@/layout/BackgroundImageLayout';
import AppUrlListener from './AppUrlListener';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';
import VersionFooter from '@/shared/ui/VersionFooter';

import { ROUTES } from '@/shared/constants';
import { useAuth } from '@/providers/AuthContext';
import { checkUserPermission } from '@/shared/lib/permissions-utils';
import { Permission } from '@/shared/model/permission';

const Home = lazy(() => import('@/pages/Home'));
const Login = lazy(() => import('@/pages/Login'));
const InvalidAccount = lazy(() => import('@/pages/InvalidAccount'));
const Admin = lazy(() => import('@/pages/Admin'));
const TimeRegistration = lazy(() => import('@/pages/TimeRegistration'));
const ParkingReservation = lazy(() => import('@/pages/ParkingReservation'));
const Report = lazy(() => import('@/pages/Report'));

const DefaultRedirect = () => {
  const { user, signedIn, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (
    signedIn &&
    user &&
    checkUserPermission(user, [Permission.CAN_ACCESS_TERMINAL_ATTENDANCE])
  ) {
    return <Navigate to={ROUTES.TimeRegistration} replace />;
  }
  return <Navigate to={ROUTES.Home} replace />;
};

const routes = [
  {
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <AppUrlListener />
        <Outlet />
        <VersionFooter />
      </Suspense>
    ),
    children: [
      {
        element: <BackgroundImageLayout />,
        children: [
          {
            path: ROUTES.TimeRegistration,
            element: (
              <LoggedInRoute
                authorizedPermissions={[
                  Permission.CAN_ACCESS_TERMINAL_ATTENDANCE,
                ]}
              >
                <TimeRegistration />
              </LoggedInRoute>
            ),
          },
        ],
      },
      {
        element: <HeaderLayout />,
        children: [
          {
            path: ROUTES.Home,
            element: (
              <LoggedInRoute>
                <Home />
              </LoggedInRoute>
            ),
          },
          {
            path: ROUTES.Admin,
            element: (
              <LoggedInRoute
                authorizedPermissions={[Permission.CAN_MANAGE_USERS]}
              >
                <Admin />
              </LoggedInRoute>
            ),
          },
          {
            path: ROUTES.Login,
            element: <Login />,
          },
          {
            path: ROUTES.InvalidAccount,
            element: <InvalidAccount />,
          },
          {
            path: ROUTES.Report,
            element: (
              <LoggedInRoute
                authorizedPermissions={[
                  Permission.CAN_GENERATE_ATTENDANCE_REPORT,
                ]}
              >
                <Report />
              </LoggedInRoute>
            ),
          },
          {
            path: ROUTES.ParkingReservation,
            element: <ParkingReservation />,
          },
          {
            path: '*',
            element: <DefaultRedirect />,
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
